/**
 * listingStatus — which held codes the market no longer trades (WORK.md item
 * 44 step 7, phase B). Reads GET /api/symbols/status, which answers from D1's
 * `instruments.status`: `delisted` or `suspended`, nothing for an active name.
 *
 * A failed or unjudged read is said, never read as "all active": `judged` is
 * false and the holdings table tells the reader the status could not be read.
 *
 * The same answer carries each code's quote currency (`currencies`, D1's
 * `base_currency`), which the price intake reads to put a series into its
 * trades' unit (lib/calc/priceBook.ts `alignQuoteUnits`).
 *
 * Only the codes the route reads are asked (lib/statusRoute.ts): a cash code
 * or a self-priced one over 40 characters has no status and no quote unit,
 * and asking one had the whole book's read refused (audit MONEY-27).
 */
import { useEffect, useState } from 'react';
import { getJsonOrThrow } from './http';
import { STATUS_MAX_CODES, STATUS_ROUTE, statusCodes } from '../../../lib/statusRoute';

export type ListingStatus = 'delisted' | 'suspended';

export interface ListingStatusAnswer {
  statuses: Record<string, ListingStatus>;
  /** False when the status could not be read at all (network, server, or D1
   *  before its first status night). Null while the first read is in flight. */
  judged: boolean | null;
}

export async function fetchListingStatus(
  codes: string[], opts: { signal?: AbortSignal } = {},
): Promise<{ statuses: Record<string, ListingStatus>; judged: boolean; currencies: Record<string, string> }> {
  const unique = statusCodes(codes);
  const statuses: Record<string, ListingStatus> = {};
  const currencies: Record<string, string> = {};
  let judged = true;
  for (let i = 0; i < unique.length; i += STATUS_MAX_CODES) {
    const qs = new URLSearchParams({ tickers: unique.slice(i, i + STATUS_MAX_CODES).join(',') });
    const j = await getJsonOrThrow<{ statuses?: Record<string, unknown>; judged?: boolean; currencies?: Record<string, unknown> }>(
      `${STATUS_ROUTE}?${qs.toString()}`, 'listing status',
      { credentials: 'include', signal: opts.signal },
    );
    if (j.judged !== true) judged = false;
    for (const [code, s] of Object.entries(j.statuses ?? {})) {
      if (s === 'delisted' || s === 'suspended') statuses[code] = s;
    }
    for (const [code, c] of Object.entries(j.currencies ?? {})) {
      if (typeof c === 'string' && c.trim()) currencies[code] = c.trim();
    }
  }
  return { statuses, judged, currencies };
}

/** Listing status of `tickers`, re-read when the set of codes changes. An
 *  answer is kept with the codes it was read for, so a changed book reads as
 *  "in flight" until its own answer lands, never as the last book's. */
export function useListingStatus(tickers: string[]): ListingStatusAnswer {
  const [read, setRead] = useState<{ key: string } & ListingStatusAnswer>({ key: '', statuses: {}, judged: null });
  const key = [...new Set(tickers.filter(Boolean))].sort().join(',');
  useEffect(() => {
    if (!key) return;
    const ctl = new AbortController();
    fetchListingStatus(key.split(','), { signal: ctl.signal }).then(
      (a) => setRead({ key, statuses: a.statuses, judged: a.judged }),
      () => { if (!ctl.signal.aborted) setRead({ key, statuses: {}, judged: false }); },
    );
    return () => ctl.abort();
  }, [key]);
  return read.key === key && key ? { statuses: read.statuses, judged: read.judged } : { statuses: {}, judged: null };
}
